import { useState } from "react";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { type ArtifactType } from "~/components/assistant/messages/artifactFormats";
import { trackEvent } from "~/utils/monitoring";

interface DownloadArtifactParams {
  artifactId: string;
  artifactType: ArtifactType;
  format: string;
  contextId?: string;
}

interface DownloadResult {
  success: boolean;
  error?: string;
}

export function useDownloadArtifact() {
  const [isDownloading, setIsDownloading] = useState(false);
  const { api, conversationId } = useAssistantContext();

  const downloadArtifact = async ({
    artifactId,
    artifactType,
    format,
    contextId,
  }: DownloadArtifactParams): Promise<DownloadResult> => {
    if (!conversationId) return { success: false, error: "No conversation ID" };

    setIsDownloading(true);

    try {
      const { download_url, file_name } = await api.downloadArtifact(
        conversationId,
        artifactId,
        format
      );

      // Fetch the file and trigger browser download
      const res = await fetch(download_url);
      if (!res.ok) {
        throw new Error(`Download failed with status ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file_name;
      a.click();
      URL.revokeObjectURL(url);

      trackEvent("analysis_agent_artifact_downloaded", {
        dataset_id: contextId || "Unknown",
        conversation_id: conversationId,
        artifact_id: artifactId,
        artifact_type: artifactType,
        format,
      });

      setIsDownloading(false);
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Something went wrong while downloading your file.";

      trackEvent("analysis_agent_artifact_download_failed", {
        dataset_id: contextId || "Unknown",
        conversation_id: conversationId,
        artifact_id: artifactId,
        artifact_type: artifactType,
        format,
        error: errorMessage,
      });

      setIsDownloading(false);
      return { success: false, error: errorMessage };
    }
  };

  return {
    downloadArtifact,
    isDownloading,
  };
}
